import { useFirestoreGeneralOrderBy, useFirestoreGeneral } from '../../../firebase/useFirestore';
import MixedBarChart from '../../../components/Visualisations/personas/MixedBarChart';
import { useEffect, useState, useContext } from 'react';
import { BarChart, Bar, Brush, ResponsiveContainer } from 'recharts';
import { db, timestamp } from '../../../firebase/config';
import { client } from "../../../hooks/Client";
import uuid from 'react-uuid';
import Tooltip from '../../../components/common/Tooltip';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import { Auth } from '../../../StateManagment/Auth';
import { useHistory } from "react-router-dom";
import PresenceTable from './PresenceTable';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import DeleteModal from "../../../components/common/DeleteModal";
import ReadOnlyGuard from "../../common/ReadOnlyGuard";
import presenceReportHours from '../../../helpers/personas/presenceReportHours';
import softDeleteDocs from '../../../helpers/softDeleteDocs';

const Presence = ({item, courseStartDate, courseEndDate}) => {
    // Context
    const [auth] = useContext(Auth);

    // State
    const [presenceData, setPresenceData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [brushRange, setBrushRange] = useState({ startIndex: 0, endIndex: 0 });
    const [graphType, setGraphType] = useState('bar');
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteDocid, setDeleteDocid] = useState('123');
    const [deleteName, setDeleteName] = useState('');

    // Hooks
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const history = useHistory();

    // Firestore
    const presenceReports = useFirestoreGeneralOrderBy(
        'PersonaPresenceReports',
        'PersonaID',
        item.ID ? item.ID : '',
        'Position',
        'asc',
      );

    const schedule = useFirestoreGeneral('PersonaShedule', 'PersonaID', item.ID ? item.ID : '');

    // Delete modal
    const deleteModalHandler = (e) => {
      const docid = e.currentTarget.dataset.docid;
      const deleteName = e.currentTarget.dataset.deletename;
      setDeleteDocid(docid);
      setDeleteName(deleteName);
      setDeleteModal(!deleteModal);
    };

    // Helper function to get the start time from the schedule
  const getStartTimeFromSchedule = (weekday) => {
    const day = schedule && schedule.filter(item => item.Day === weekday);
    return day[0]?.StartTime;
  };

  // Helper function to get the end time from the schedule
  const getEndTimeFromSchedule = (weekday) => {
    const day = schedule && schedule.filter(item => item.Day === weekday);
    return day[0]?.EndTime;
  };

  // Helper function to format a date as 'YYYY-MM-DD' for easy comparison
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to format the date to a Dutch string
  const formatDateToDutchString = (date) => {
    const daysOfWeek = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
    const monthsOfYear = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
    const dayName = daysOfWeek[date.getDay()];
    const day = date.getDate();
    const monthName = monthsOfYear[date.getMonth()];
    const year = date.getFullYear();
    return `${dayName} ${day} ${monthName} ${year}`;
  };

  // Helper function to normalize the date
  const normalizeDate = (reportDate) => {
    if (typeof reportDate === 'string') {
      return reportDate;
    }
    if (reportDate.seconds !== undefined && reportDate.nanoseconds !== undefined) {
      const milliseconds = reportDate.seconds * 1000 + reportDate.nanoseconds / 1000000;
      const dateObject = new Date(milliseconds);
      return formatDateToDutchString(dateObject);
    }
  };

  // Helper function to get the simplified date
  const getSimplifiedDate = (date) => {
    const dateObject = new Date(date);
    return dateObject.getTime();
  };

  // Helper function to parse a custom date string like "maandag 22 juli 2024" into a JavaScript Date object
  const parseCustomDate = (customDateString) => {
    const monthsOfYear = {
      januari: 0, februari: 1, maart: 2, april: 3, mei: 4, juni: 5,
      juli: 6, augustus: 7, september: 8, oktober: 9, november: 10, december: 11
    };
    const parts = customDateString ? customDateString.split(' ') : '';
    const day = parseInt(parts[1]);
    const month = monthsOfYear[parts[2]];
    const year = parseInt(parts[3]);
    return new Date(year, month, day);
  };

  // Helper function to get the scheduled presence for a specific date
  const getSheduledPresence = (startTime, endTime) => {
    const start = new Date(`1970-01-01T${startTime}:00`);
    const end = new Date(`1970-01-01T${endTime}:00`);
    const diff = end - start;
    return diff / 1000 / 60 / 60;
  };

  // Build presence data from ALL reports — no date filter
  const createPresenceData = () => {
    if (!presenceReports || presenceReports.length === 0) {
      setLoading(false);
      return;
    }

    const data = [];

    presenceReports.forEach(report => {
      if (report.Date === undefined) return;

      const simplifiedDate = getSimplifiedDate(report.Date);
      // Dezelfde som als het maandrooster en de jaarweergave gebruiken; die
      // stond hier als derde kopie en week af op ontbrekende correctievelden
      // en op een dienst over middernacht.
      // AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
      const presentHours = presenceReportHours(report).hours;

      data.push({
        date: normalizeDate(report.Date),
        type: report.Type,
        absent: report.Absent,
        reason: report.Reason,
        Aanwezigheid: presentHours,
        Geroosterd: report.Type === 'absent' ? 0 : report.Schedule - presentHours,
        Afwezig: report.Type === 'absent' ? report.Schedule : 0,
        present: presentHours,
        schedule: report.Schedule,
        id: report.ID,
        personaId: report.PersonaID,
        simplifiedDate: simplifiedDate,
        total: presentHours - report.Schedule,
        Position: report.Position,
        docid: report.docid
      });
    });

    const orderedData = data.sort((a, b) => a.simplifiedDate - b.simplifiedDate);
    setPresenceData(orderedData);
    // Reset brush to show all data
    setBrushRange({ startIndex: 0, endIndex: orderedData.length - 1 });
    setLoading(false);
  };

  useEffect(() => {
    createPresenceData();
  }, [presenceReports]);

  // Compute totals from the brush-selected slice
  const visibleData = presenceData.slice(brushRange.startIndex, brushRange.endIndex + 1);

  const totalPresent = visibleData.reduce((acc, row) => {
    const hours = row.present;
    return isNaN(hours) ? acc : acc + hours;
  }, 0);

  const totalSheduledPresence = visibleData.reduce((acc, row) => {
    const scheduled = Number(row.schedule);
    return isNaN(scheduled) ? acc : acc + scheduled;
  }, 0);

  // 'Verwijder alle rapporten' is één handeling: de hele historie gaat samen
  // naar de prullenbak en komt daar samen terug (ADR-008). Het eerste rapport
  // is de ouder.
  // AI-CONTEXT: See docs/pitfalls.md#ui-soft-delete-promises-a-trash-it-is-not-in
  const deleteAllReports = async () => {
    const docids = (presenceReports || []).map(report => report.docid).filter(Boolean);
    await softDeleteDocs({
      collection: 'PersonaPresenceReports',
      docids,
      authId: auth?.ID,
      title: `Aanwezigheidsrapporten van ${item.Name || 'deelnemer'}`,
    });
  };

  // Save the calculated presence data to the database
  const saveCalculatedFields = () => {
    presenceReports.forEach(report => {
      if(report.Schedule !== undefined) return;
      const { Start, End, docid } = report;
      const scheduleValue = getSheduledPresence(Start, End);
      db.collection('PersonaPresenceReports')
        .doc(docid)
        .update({ Schedule: scheduleValue })
        .then(() => console.log(`Report ${docid} updated successfully.`))
        .catch(error => console.error(`Error updating report ${docid}:`, error));
    });
  };

  useEffect(() => {
    if (presenceReports && presenceReports.length > 0) {
      saveCalculatedFields();
    }
  }, [presenceReports]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const tooltipType = (type) => {
        if (type === 'correct') return 'Correct';
        if (type === 'absent') return 'Afwezig';
        if (type === 'correction') return 'Gecorrigeerd';
      };

      return (
        <div className="custom-tooltip">
          <p className="label">{payload[0].payload.date ? payload[0].payload.date : 'Geen datum'}</p>
          <h2 className="label" id='presence-tooltip-type'>{tooltipType(payload[0].payload.type)}</h2>
          <p className="desc" style={{ display: payload[0].payload.type === 'absent' ? 'none' : 'block' }}>Geroosterd: {payload[0].payload.schedule}</p>
          <p className="desc" style={{ display: payload[0].payload.type === 'absent' ? 'none' : 'block' }}>Aanwezig: {payload[0].payload.present}</p>
          <p className="desc" style={{ display: payload[0].payload.reason === '' ? 'none' : 'block' }}>Reden: {payload[0].payload.reason}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className='dashboard-container'>
        <h2>Aanwezigheid</h2>

        <div id='presence-data-outer-container'>
          <div className='presence-data-container'>
            <p><b>Totaal geroosterd</b></p>
            <p>{totalSheduledPresence} uur</p>
          </div>
          <div className='presence-data-container'>
            <p><b>Totaal aanwezig</b></p>
            <p>{totalPresent} uur</p>
          </div>
          <div className='presence-data-container'>
            <p><b>Aanwezig - geroosterd</b></p>
            <p>
              {(totalPresent - totalSheduledPresence > 0 ? '+' : '')
                + (totalPresent - totalSheduledPresence)
              } uur
            </p>
          </div>
        </div>

        {graphType === 'table' &&
       <PresenceTable
       item={item}
       length={presenceReports.length}
       getStartTimeFromSchedule={getStartTimeFromSchedule}
       getEndTimeFromSchedule={getEndTimeFromSchedule}
       auth={auth}
       personaId={item.ID}
       visibleData={visibleData}
      />}

        {graphType === 'bar' &&
        <MixedBarChart
        data={visibleData}
        customTooltip={<CustomTooltip />}
        />}

            {/* Brush strip — always visible */}
        <ResponsiveContainer width="100%" height={50}>
          <BarChart data={presenceData} margin={{ top: 0, right: 30, left: 20, bottom: 0 }}>
            <Bar dataKey="Aanwezigheid" fill="transparent" />
            <Brush
              dataKey="date"
              height={20}
              stroke="#206c7f"
              startIndex={brushRange?.startIndex ?? 0}
              endIndex={brushRange?.endIndex ?? (presenceData?.length ? presenceData.length - 1 : 0)}
              onChange={setBrushRange}
            />
          </BarChart>
        </ResponsiveContainer>

        <div id='persona-presence-options-container'>
          <Tooltip content={'Bekijk in grafiek'} top='35px' width='30px'>
              <BarChartOutlinedIcon onClick={() => setGraphType('bar')} />
          </Tooltip>
          <Tooltip content={'bekijk in tabel'} top='35px' width='30px'>
              <TableChartOutlinedIcon onClick={() => setGraphType('table')}/>
          </Tooltip>
          <Tooltip content='Print aanwezigheidsrapport' width={'30px'} top={'-80px'}>
            <LocalPrintshopOutlinedIcon onClick={() => history.push(`/${client}/printpresencereport/${item.ID}`)}/>
          </Tooltip>
          <ReadOnlyGuard edit="personas.edit">
            {(canEdit) => canEdit && (
              <Tooltip content='Verwijder alle rapporten' width={'30px'} top={'-80px'}>
                {/* Het venster moet benoemen wát er weggaat: dit verwijdert de
                    hele aanwezigheidshistorie van deze deelnemer, niet één rij. */}
                <DeleteOutlineOutlinedIcon onClick={() => {
                  setDeleteName(`alle ${presenceReports?.length || 0} aanwezigheidsrapporten van ${item.Name || 'deze deelnemer'}`)
                  setDeleteModal(true)
                }} />
              </Tooltip>
            )}
          </ReadOnlyGuard>
        </div>
        
        <DeleteModal deleteModal={deleteModal} setDeleteModal={setDeleteModal} deleteItem={deleteAllReports} deleteName={deleteName} softDelete />
      </div>

  )
}

export default Presence
